import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { UserMenu } from "@/components/UserMenu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Calendar,
  Clock,
  Home,
  Menu,
  Users,
  FileText,
  CalendarDays,
  Sprout,
  Leaf,
  ArrowRight,
  BookOpen,
  Video,
  MapPin,
  Sun,
  ShoppingBag,
  Heart,
  Eye,
  Mail,
  Share2,
  Gift,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

interface TodayAppointment {
  id: string;
  appointment_date: string;
  appointment_time: string | null;
  duration_minutes: number;
  is_online: boolean | null;
  service_type: string | null;
  first_name: string | null;
  last_name: string | null;
  patient_id: string | null;
  patient?: {
    name: string;
  };
}

export const ProNavigation = () => {
  const [todayAppointments, setTodayAppointments] = useState<TodayAppointment[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadTodayAppointments();
  }, []);

  const loadTodayAppointments = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const { data, error } = await supabase
        .from("appointments")
        .select(
          `
          id,
          appointment_date,
          appointment_time,
          duration_minutes,
          is_online,
          service_type,
          first_name,
          last_name,
          patient_id,
          patient:patients(name)
        `
        )
        .or(`practitioner_id.eq.${user.id},practitioner_id.is.null`)
        .in("status", ["scheduled", "confirmed"])
        .gte("appointment_date", today.toISOString())
        .lt("appointment_date", tomorrow.toISOString())
        .order("appointment_time");

      if (error) throw error;
      setTodayAppointments(data || []);
    } catch (error: any) {
      console.error("Erreur chargement rendez-vous:", error);
    }
  };

  const getAppointmentColor = (appointment: TodayAppointment) => {
    if (appointment.is_online) {
      return "bg-blue-500/20 border-blue-500/30 text-blue-700 dark:text-blue-300";
    }
    return "bg-green-500/20 border-green-500/30 text-green-700 dark:text-green-300";
  };

  const getPatientName = (appointment: TodayAppointment) => {
    if (appointment.patient?.name) {
      return appointment.patient.name;
    }
    if (appointment.first_name && appointment.last_name) {
      return `${appointment.first_name} ${appointment.last_name}`;
    }
    if (appointment.first_name) {
      return appointment.first_name;
    }
    return "Patient inconnu";
  };

  const handleAppointmentClick = (appointmentId: string) => {
    navigate(`/appointments?id=${appointmentId}`);
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Header avec menu utilisateur */}
      <div className="container mx-auto px-3 sm:px-4 pt-3 sm:pt-4">
        <div className="flex justify-end items-center gap-2">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-[10px] sm:text-xs text-muted-foreground hover:text-primary h-8 sm:h-9 px-2 sm:px-3">
              <Home className="w-3 h-3 mr-1" />
              <span className="hidden xs:inline">Site vitrine</span>
              <span className="xs:hidden">Site</span>
            </Button>
          </Link>

          {/* Menu hamburger de navigation rapide */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 sm:gap-2 h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm">
                <Menu className="w-4 h-4" />
                <span className="hidden sm:inline">Navigation</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] bg-gradient-to-b from-card to-background overflow-y-auto">
              <div className="flex flex-col gap-4 sm:gap-5 mt-6 sm:mt-8">
                <div className="pb-4 border-b border-border/50">
                  <h2 className="text-lg sm:text-xl font-bold font-cinzel">
                    <span className="text-secondary">REGARD</span>
                    <span className="text-primary"> DE NATURE</span>
                  </h2>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 tracking-wide">Navigation rapide</p>
                </div>

                <nav className="flex flex-col gap-1">
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Home className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Accueil</span>
                    </div>
                  </Link>
                  <Link to="/patients" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Fiche Consultants</span>
                    </div>
                  </Link>
                  <Link to="/appointments" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Calendar className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Rendez-vous</span>
                    </div>
                  </Link>
                  <Link to="/vitality" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <FileText className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Bilan de vitalité</span>
                    </div>
                   </Link>
                  <Link to="/vitality-scoring" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <FileText className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Scoring Vitalité</span>
                    </div>
                  </Link>
                  <Link to="/session-report" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <FileText className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Compte rendu de séance</span>
                    </div>
                  </Link>
                  <Link to="/availability-settings" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Clock className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Horaires d'ouverture</span>
                    </div>
                  </Link>
                  <Link to="/locations" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Lieux de consultation</span>
                    </div>
                  </Link>
                  <Link to="/billing" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <FileText className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Facturation</span>
                    </div>
                  </Link>
                  <Link to="/events-management" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <CalendarDays className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Événements & Stages</span>
                    </div>
                  </Link>
                  <Link to="/products-management" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <ShoppingBag className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Boutique / Produits</span>
                    </div>
                  </Link>
                  <Link to="/accompaniments-management" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <Heart className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Accompagnements</span>
                    </div>
                  </Link>
                  <Link to="/seasonal-wellness" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <Sun className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Bien-être saisonnier</span>
                    </div>
                  </Link>
                  <Link to="/gift-cards" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <Gift className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Cartes Cadeaux</span>
                    </div>
                  </Link>
                  <Link to="/iridology" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Eye className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Iridologie</span>
                    </div>
                  </Link>
                  <Link to="/plants" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Sprout className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Plantes médicinales</span>
                    </div>
                  </Link>
                  <Link to="/bach-flowers" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <Leaf className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Fleurs de Bach</span>
                    </div>
                  </Link>
                  <Link to="/app" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <ArrowRight className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Commencer l'analyse</span>
                    </div>
                  </Link>
                  <Link to="/library" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <BookOpen className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Bibliothèque de traitements</span>
                    </div>
                  </Link>
                  <Link to="/newsletter-management" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                        <Mail className="w-4 h-4 text-secondary" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Newsletter</span>
                    </div>
                  </Link>
                  <Link to="/social-publishing" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/5 transition-colors group">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <Share2 className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-sm font-medium text-foreground">Publication Réseaux</span>
                    </div>
                  </Link>
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          <UserMenu />
        </div>
      </div>

      {/* Rendez-vous du jour - panneau flottant en bas à droite - caché sur mobile petit, visible sur tablette+ */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 group hidden sm:block">
        <Card className="bg-card/95 backdrop-blur-sm border-primary/20 shadow-lg hover:shadow-xl transition-all duration-300 w-14 sm:w-16 group-hover:w-80 sm:group-hover:w-96 overflow-hidden animate-fade-in">
          <CardHeader className="p-2 sm:p-3 group-hover:p-3 sm:group-hover:p-4 transition-all">
            <CardTitle className="flex items-center gap-2 text-xs sm:text-sm group-hover:text-sm sm:group-hover:text-base transition-all">
              <div className="w-8 h-8 sm:w-10 sm:h-10 group-hover:w-10 group-hover:h-10 sm:group-hover:w-12 sm:group-hover:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all relative">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 group-hover:w-5 group-hover:h-5 sm:group-hover:w-6 sm:group-hover:h-6 text-primary animate-pulse" />
                {todayAppointments.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-primary text-primary-foreground rounded-full text-[10px] sm:text-xs flex items-center justify-center font-bold">
                    {todayAppointments.length}
                  </span>
                )}
              </div>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap font-semibold text-xs sm:text-sm">
                RDV du jour {todayAppointments.length > 0 && `(${todayAppointments.length})`}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 sm:p-3 sm:pt-0 group-hover:p-3 group-hover:pt-0 sm:group-hover:p-4 sm:group-hover:pt-0 opacity-0 group-hover:opacity-100 transition-opacity">
            {todayAppointments.length === 0 ? (
              <div className="text-center py-3 sm:py-4 text-muted-foreground">
                <p className="text-xs sm:text-sm">Aucun rendez-vous aujourd'hui</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {todayAppointments.map((apt) => (
                  <button
                    key={apt.id}
                    onClick={() => handleAppointmentClick(apt.id)}
                    className={`w-full p-2 sm:p-3 rounded-lg border-2 transition-all hover:scale-105 hover:shadow-md ${getAppointmentColor(
                      apt
                    )}`}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/50 dark:bg-black/20 flex items-center justify-center">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                      </div>
                      <div className="flex-1 text-left space-y-0.5 sm:space-y-1">
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-[10px] sm:text-xs font-bold">
                            {apt.appointment_time || format(parseISO(apt.appointment_date), "HH:mm", { locale: fr })}
                          </Badge>
                          {apt.is_online ? (
                            <Badge variant="outline" className="text-[10px] sm:text-xs gap-1">
                              <Video className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              Visio
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] sm:text-xs gap-1">
                              <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              Cabinet
                            </Badge>
                          )}
                        </div>
                        <p className="font-semibold text-xs sm:text-sm">{getPatientName(apt)}</p>
                        {apt.service_type && (
                          <p className="text-[10px] sm:text-xs opacity-80 truncate">{apt.service_type}</p>
                        )}
                        <p className="text-[10px] sm:text-xs opacity-70">{apt.duration_minutes} min</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Version mobile du panneau RDV - bouton fixe en bas à droite */}
      <div className="fixed bottom-4 right-4 z-50 sm:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              size="icon"
              className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg relative"
            >
              <Calendar className="w-5 h-5" />
              {todayAppointments.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full text-xs flex items-center justify-center font-bold">
                  {todayAppointments.length}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[60vh] rounded-t-2xl">
            <div className="py-4">
              <h3 className="text-lg font-semibold text-primary mb-4">Rendez-vous du jour</h3>
              {todayAppointments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Aucun rendez-vous aujourd'hui</p>
                </div>
              ) : (
                <div className="space-y-3 overflow-y-auto max-h-[calc(60vh-100px)]">
                  {todayAppointments.map((apt) => (
                    <button
                      key={apt.id}
                      onClick={() => handleAppointmentClick(apt.id)}
                      className={`w-full p-3 rounded-lg border-2 transition-all active:scale-95 ${getAppointmentColor(apt)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-white/50 dark:bg-black/20 flex items-center justify-center">
                            <Clock className="w-5 h-5" />
                          </div>
                        </div>
                        <div className="flex-1 text-left space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="secondary" className="text-xs font-bold">
                              {apt.appointment_time || format(parseISO(apt.appointment_date), "HH:mm", { locale: fr })}
                            </Badge>
                            {apt.is_online ? (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Video className="w-3 h-3" />
                                Visio
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs gap-1">
                                <MapPin className="w-3 h-3" />
                                Cabinet
                              </Badge>
                            )}
                          </div>
                          <p className="font-semibold text-sm">{getPatientName(apt)}</p>
                          {apt.service_type && <p className="text-xs opacity-80">{apt.service_type}</p>}
                          <p className="text-xs opacity-70">{apt.duration_minutes} min</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
